/**
 * SpeakerIdentitySelector Component
 *
 * Radio group selector for identifying speakers in the binding wizard.
 * Shows the user option and a list of conversation partners.
 * Pre-selects the suggested partner if provided.
 * Optionally allows inline creation of new conversation partners.
 */

import { useState } from "react";
import { User, Users, Plus, Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { SpeakerIdentity } from "@/types/speaker-binding";
import type {
  ConversationPartner,
  ConversationPartnerCreate,
  ConversationPartnerType,
} from "@/types/conversationPartner";

interface SpeakerIdentitySelectorProps {
  speakerLabel: string;
  selectedIdentity: SpeakerIdentity;
  onIdentityChange: (identity: SpeakerIdentity) => void;
  /** Selected partner ID (for "partner" identity) */
  selectedPartnerId?: string | null;
  onPartnerIdChange?: (partnerId: string | null) => void;
  /** List of conversation partners to show as options */
  conversationPartners?: ConversationPartner[];
  /** Suggested partner from reflection creation */
  suggestedPartnerId?: string | null;
  /** Whether the reflection was created with primary partner selected */
  isWithPrimaryPartner?: boolean;
  /** Async callback to create a new partner; returns the created partner */
  onCreatePartner?: (
    data: ConversationPartnerCreate
  ) => Promise<ConversationPartner>;
  /** Whether the partner limit (10) has been reached */
  isPartnerLimitReached?: boolean;
  className?: string;
}

export function SpeakerIdentitySelector({
  speakerLabel,
  selectedIdentity,
  onIdentityChange,
  selectedPartnerId,
  onPartnerIdChange,
  conversationPartners = [],
  suggestedPartnerId,
  isWithPrimaryPartner,
  onCreatePartner,
  isPartnerLimitReached = false,
  className = "",
}: SpeakerIdentitySelectorProps) {
  // Inline form state
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formPronouns, setFormPronouns] = useState("");
  const [formType, setFormType] = useState<ConversationPartnerType>("other");
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Find the primary partner
  const primaryPartner = conversationPartners.find((p) => p.is_primary);

  // Determine the suggested partner
  const suggestedPartner = isWithPrimaryPartner
    ? primaryPartner
    : conversationPartners.find((p) => p.id === suggestedPartnerId);

  // Handle selection of user
  const handleUserSelect = () => {
    onIdentityChange("user");
    onPartnerIdChange?.(null);
  };

  // Handle selection of a partner
  const handlePartnerSelect = (partner: ConversationPartner) => {
    onIdentityChange("partner");
    onPartnerIdChange?.(partner.id);
  };

  // Check if a partner is selected
  const isPartnerSelected = (partnerId: string) =>
    selectedIdentity === "partner" && selectedPartnerId === partnerId;

  // Reset the inline form
  const resetForm = () => {
    setShowForm(false);
    setFormName("");
    setFormPronouns("");
    setFormType("other");
    setFormError(null);
  };

  // Handle inline partner creation
  const handleCreatePartner = async () => {
    if (!onCreatePartner) return;

    const trimmedName = formName.trim();
    if (!trimmedName) {
      setFormError("Name is required");
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const newPartner = await onCreatePartner({
        name: trimmedName,
        pronouns: formPronouns.trim() || undefined,
        partner_type: formType,
      });

      // Auto-select the newly created partner
      onIdentityChange("partner");
      onPartnerIdChange?.(newPartner.id);

      resetForm();
    } catch {
      setFormError("Failed to create partner. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  // Relationship type options for the inline form
  const typeOptions: { value: ConversationPartnerType; label: string }[] = [
    { value: "other", label: "Other" },
    { value: "primary_partner", label: "Primary Partner" },
    { value: "friend", label: "Friend" },
    { value: "family", label: "Family" },
    { value: "therapist", label: "Therapist" },
  ];

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="space-y-3">
        <Label className="text-sm font-medium text-foreground">
          Who is {speakerLabel}?
        </Label>

        <RadioGroup
          value={
            selectedIdentity === "user"
              ? "user"
              : selectedIdentity === "partner" && selectedPartnerId
                ? `partner-${selectedPartnerId}`
                : ""
          }
          onValueChange={(value) => {
            if (value === "user") {
              handleUserSelect();
            } else if (value.startsWith("partner-")) {
              const partnerId = value.replace("partner-", "");
              const partner = conversationPartners.find(
                (p) => p.id === partnerId
              );
              if (partner) {
                handlePartnerSelect(partner);
              }
            }
          }}
        >
          {/* User option */}
          <div className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-border transition-colors">
            <RadioGroupItem value="user" id={`${speakerLabel}-user`} />
            <Label
              htmlFor={`${speakerLabel}-user`}
              className="flex items-center gap-2 cursor-pointer flex-1"
            >
              <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium">Me (the user)</span>
            </Label>
          </div>

          {/* Conversation partners */}
          {conversationPartners.length > 0 ? (
            conversationPartners.map((partner) => {
              const isSuggested = partner.id === suggestedPartner?.id;
              const isSelected = isPartnerSelected(partner.id);

              return (
                <div
                  key={partner.id}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                    isSelected
                      ? "border-green-500 bg-green-50 dark:bg-green-950"
                      : isSuggested
                        ? "border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-950/50"
                        : "border-border hover:border-border"
                  }`}
                >
                  <RadioGroupItem
                    value={`partner-${partner.id}`}
                    id={`${speakerLabel}-partner-${partner.id}`}
                  />
                  <Label
                    htmlFor={`${speakerLabel}-partner-${partner.id}`}
                    className="flex items-center gap-2 cursor-pointer flex-1"
                  >
                    <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium">{partner.name}</span>
                    {partner.pronouns && (
                      <span className="text-xs text-muted-foreground">
                        ({partner.pronouns})
                      </span>
                    )}
                    {isSuggested && (
                      <Badge
                        variant="secondary"
                        className="ml-auto text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                      >
                        Selected for this conversation
                      </Badge>
                    )}
                    {partner.is_primary && !isSuggested && (
                      <Badge
                        variant="outline"
                        className="ml-auto text-xs text-muted-foreground"
                      >
                        Primary
                      </Badge>
                    )}
                  </Label>
                </div>
              );
            })
          ) : !onCreatePartner ? (
            <div className="rounded-lg border border-border bg-muted p-4 text-center">
              <p className="text-sm text-muted-foreground">
                No conversation partners saved yet.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Add partners in your account settings.
              </p>
            </div>
          ) : null}
        </RadioGroup>

        {/* Inline add new person */}
        {onCreatePartner && (
          <div className="mt-2">
            {isPartnerLimitReached ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                Maximum 10 partners reached
              </p>
            ) : !showForm ? (
              <button
                type="button"
                onClick={() => setShowForm(true)}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground hover:border-border hover:text-foreground transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add new person
              </button>
            ) : (
              <div className="rounded-lg border border-border bg-muted p-4 space-y-3">
                <div>
                  <Label
                    htmlFor={`${speakerLabel}-new-name`}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Name *
                  </Label>
                  <Input
                    id={`${speakerLabel}-new-name`}
                    value={formName}
                    onChange={(e) => {
                      setFormName(e.target.value);
                      if (formError) setFormError(null);
                    }}
                    placeholder="Enter name"
                    className="mt-1 h-9 text-sm"
                    autoFocus
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`${speakerLabel}-new-pronouns`}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Pronouns
                  </Label>
                  <Input
                    id={`${speakerLabel}-new-pronouns`}
                    value={formPronouns}
                    onChange={(e) => setFormPronouns(e.target.value)}
                    placeholder="e.g. she/her, he/him, they/them"
                    className="mt-1 h-9 text-sm"
                  />
                </div>

                <div>
                  <Label
                    htmlFor={`${speakerLabel}-new-type`}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Relationship
                  </Label>
                  <select
                    id={`${speakerLabel}-new-type`}
                    value={formType}
                    onChange={(e) =>
                      setFormType(e.target.value as ConversationPartnerType)
                    }
                    className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    {typeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formError && (
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {formError}
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreatePartner}
                    disabled={formLoading}
                    className="flex-1"
                  >
                    {formLoading ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create & Select"
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={resetForm}
                    disabled={formLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
